class AuraRequiredSizeChoice extends HTMLElement {
  #controller;
  #fieldsets = [];
  #form;
  #idInput;
  #mainButton;
  #paymentRegion;
  #picker;
  #released = false;
  #stickyButton;

  connectedCallback() {
    this.#released = false;
    this.#controller = new AbortController();
    queueMicrotask(() => this.#initialize());
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #initialize() {
    if (!this.isConnected) return;

    const formId = this.getAttribute('form');
    const scope = this.closest('.hdt-main-product') || document;

    this.#form = document.getElementById(formId);
    this.#picker = Array.from(scope.querySelectorAll('hdt-variant-picker')).find(
      (picker) => picker.getAttribute('form') === formId
    );

    if (!this.#form || !this.#picker) return;

    this.#fieldsets = Array.from(this.#picker.querySelectorAll('fieldset'));
    this.#idInput = this.#form.querySelector('input[name="id"]');
    this.#mainButton = this.#form.querySelector('button[type="submit"][name="add"]');
    this.#paymentRegion = this.#form.querySelector('[data-aura-required-payment]');
    this.#stickyButton = Array.from(document.querySelectorAll('hdt-sticky-add-to-cart')).find(
      (sticky) => sticky.dataset.form === formId
    )?.querySelector('[ref="variantButton"]');

    this.#clearSelections();
    this.#lockPurchaseControls();
    this.#removeFallbackVariantFromUrl();

    const { signal } = this.#controller;
    this.#form.addEventListener('submit', this.#handleSubmit, { capture: true, signal });
    this.#form.addEventListener('product:updateNow', this.#handleProductUpdate, { signal });
    this.#picker.addEventListener('change', this.#handleChange, { signal });
  }

  #clearSelections() {
    this.#picker.setAttribute('no-pick', '');
    this.#picker.isNoPick = true;

    this.#picker.querySelectorAll('input[is-value][type="radio"]').forEach((input) => {
      input.checked = false;
      input.defaultChecked = false;
      input.removeAttribute('checked');
    });

    this.#picker.querySelectorAll('button[is-value][aria-selected="true"]').forEach((button) => {
      button.setAttribute('aria-selected', 'false');
    });

    this.#picker.querySelectorAll('input[trigger-value]').forEach((input) => {
      input.value = '';
      input.dataset.valueId = '';
    });
  }

  #lockPurchaseControls() {
    const buttonText = this.dataset.buttonText;

    if (this.#idInput) {
      this.#idInput.value = '';
      this.#idInput.disabled = true;
    }

    if (this.#mainButton) {
      this.#mainButton.disabled = true;
      this.#mainButton.dataset.hover = buttonText;
      const label = this.#mainButton.querySelector(':scope > span');
      if (label) label.textContent = buttonText;
    }

    if (this.#stickyButton) {
      this.#stickyButton.disabled = true;
      Array.from(this.#stickyButton.children).forEach((content) => {
        const label = content.querySelector('span');
        if (label) label.textContent = buttonText;
      });
    }

    if (this.#paymentRegion) {
      this.#paymentRegion.hidden = true;
      this.#paymentRegion.setAttribute('inert', '');
      this.#paymentRegion.setAttribute('aria-hidden', 'true');
      this.#paymentRegion.style.display = 'none';
    }
  }

  #removeFallbackVariantFromUrl() {
    if (!this.#picker.hasAttribute('update-url') || !history.replaceState) return;

    const url = new URL(window.location.href);
    if (!url.searchParams.has('variant')) return;

    url.searchParams.delete('variant');
    history.replaceState(history.state, '', url.toString());
    document.dispatchEvent(new CustomEvent('hdt:updateUrl'));
  }

  #isChosen(fieldset) {
    if (fieldset.querySelector('input[is-value][type="radio"]:checked')) return true;
    if (fieldset.querySelector('button[is-value][aria-selected="true"], a[is-value][selected]')) return true;

    const hiddenInput = fieldset.querySelector('input[trigger-value], input[is-value][type="hidden"]');
    return Boolean(hiddenInput?.value && hiddenInput.dataset.valueId);
  }

  #firstMissingFieldset() {
    return this.#fieldsets.find((fieldset) => !this.#isChosen(fieldset));
  }

  #handleChange = () => {
    this.#fieldsets.forEach((fieldset) => {
      if (this.#isChosen(fieldset)) fieldset.removeAttribute('aria-invalid');
    });

    queueMicrotask(() => {
      if (this.#released || !this.isConnected) return;

      if (this.#firstMissingFieldset() || !this.#idInput?.value) {
        this.#lockPurchaseControls();
        return;
      }

      this.#releasePurchaseControls();
    });
  };

  #handleSubmit = (event) => {
    if (this.#released) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#lockPurchaseControls();
    this.#focusFirstMissingFieldset();
  };

  #handleProductUpdate = (event) => {
    if (this.dataset.preorderTag !== 'true' || !event.detail.variant.available) return;

    const label = this.#mainButton?.firstElementChild;
    if (!label) return;

    const text = window.themeHDN?.strings?.preOrder || 'Pre-order';
    label.textContent = text;
    this.#mainButton.dataset.hover = text;
  };

  #focusFirstMissingFieldset() {
    const fieldset = this.#firstMissingFieldset() || this.#fieldsets[0];
    if (!fieldset) return;

    fieldset.setAttribute('aria-invalid', 'true');
    fieldset.setAttribute('tabindex', '-1');
    this.textContent = '';

    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.textContent = this.dataset.hintText;
      fieldset.focus();
    });
  }

  #releasePurchaseControls() {
    this.#released = true;
    this.setAttribute('released', '');
    this.#idInput.disabled = false;

    if (this.#paymentRegion) {
      this.#paymentRegion.hidden = false;
      this.#paymentRegion.removeAttribute('inert');
      this.#paymentRegion.removeAttribute('aria-hidden');
      this.#paymentRegion.style.display = 'contents';
    }

    this.#fieldsets.forEach((fieldset) => {
      fieldset.removeAttribute('aria-invalid');
      const describedBy = (fieldset.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && id !== this.id);

      if (describedBy.length) {
        fieldset.setAttribute('aria-describedby', describedBy.join(' '));
      } else {
        fieldset.removeAttribute('aria-describedby');
      }
    });

    this.textContent = '';
  }
}

if (!customElements.get('aura-required-size-choice')) {
  customElements.define('aura-required-size-choice', AuraRequiredSizeChoice);
}
